import { useState } from 'react';
import { useAuthStore } from '../../auth/store';
import { usePermissions, Permission } from '../../../shared/hooks/usePermissions';
import { formatMinor } from '../../../shared/utils/money';
import { useCharterOrders } from '../hooks/useCharter';
import { OrderDetailModal } from '../components/OrderDetailModal';
import { BookingStatusBadge, PaymentStatusBadge } from '../components/CharterStatusBadge';
import {
  CharterHeader,
  EmptyState,
  ErrorState,
  LoadingState,
  SelectMerchantPrompt,
  TableCard,
  TD,
  TD_MUTED,
  TH,
} from '../components/CharterPage';
import { formatWat } from '../components/charterFormat';

export function CharterOrdersPage() {
  const selectedTenantId = useAuthStore((s) => s.selectedTenantId);
  const { hasPermission } = usePermissions();
  const canManage = hasPermission(Permission.CHARTER_MANAGE);

  const { data: orders = [], isLoading, error } = useCharterOrders(selectedTenantId);
  const [openId, setOpenId] = useState<string | null>(null);

  return (
    <div className="py-6 max-w-6xl mx-auto px-2">
      <CharterHeader
        title="Charter · Orders"
        subtitle="Package orders for this provider. Open one to view intake, referenced work, refund decisions and session scheduling."
      />

      {!selectedTenantId ? (
        <SelectMerchantPrompt what="their Charter package orders" />
      ) : isLoading ? (
        <LoadingState label="Loading orders…" />
      ) : error ? (
        <ErrorState label="Failed to load orders." />
      ) : orders.length === 0 ? (
        <EmptyState label="No package orders yet." />
      ) : (
        <TableCard>
          <table className="w-full">
            <thead>
              <tr className="border-b border-border bg-muted text-left">
                <th className={TH}>Customer</th>
                <th className={TH}>Status</th>
                <th className={TH}>Payment</th>
                <th className={TH}>Fulfilment</th>
                <th className={TH}>Balance due</th>
                <th className={TH}></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {orders.map((o) => (
                <tr key={o.id} className="hover:bg-muted transition-colors">
                  <td className={TD}>
                    <span className="font-medium">{o.customer?.name ?? '—'}</span>
                    <span className="block text-xs text-muted-foreground">{o.customer?.email}</span>
                  </td>
                  <td className="px-6 py-4">
                    <BookingStatusBadge status={o.status} />
                  </td>
                  <td className="px-6 py-4">
                    <PaymentStatusBadge status={o.paymentStatus} />
                  </td>
                  <td className={TD_MUTED}>
                    {o.requiresSlot === false ? 'Async' : o.slot ? formatWat(o.slot.startsAt) : 'Unscheduled'}
                  </td>
                  <td className={TD_MUTED}>{o.remainingBalance ? formatMinor(o.remainingBalance, o.currency) : '—'}</td>
                  <td className="px-6 py-4">
                    <button onClick={() => setOpenId(o.id)} className="text-xs text-blue-600 hover:underline">
                      {canManage ? 'Manage' : 'View'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableCard>
      )}

      {openId && (
        <OrderDetailModal
          orderId={openId}
          tenantId={selectedTenantId}
          canManage={canManage}
          onClose={() => setOpenId(null)}
        />
      )}
    </div>
  );
}
